import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import api from "../../utils/api";
import styles from "./NewUserConnectionPage.module.scss";

interface User {
  value: string; // id
  label: string; // name + email
}

const NewUserConnectionPage: React.FC = () => {
  const [userOne, setUserOne] = useState<User | null>(null);
  const [userTwo, setUserTwo] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  const searchUsers = async (inputValue: string) => {
    if (!inputValue || inputValue.length < 2) {
      console.log("[searchUsers] Input too short:", inputValue);
      return [];
    }
    try {
      const res = await api.get<{ users: { id: string; firstname: string; lastname: string; email: string }[] }>(
        `/core/users/search?query=${encodeURIComponent(inputValue)}`
      );
      console.log("[searchUsers] Query:", inputValue, "Response:", res.data);
      return res.data.users.map((user) => ({
        value: user.id,
        label: `${user.firstname} ${user.lastname} (${user.email})`,
      }));
    } catch (err: any) {
      console.error("[searchUsers] Error:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to search users");
      return [];
    }
  };

  const handleSubmit = async () => {
    if (!userOne || !userTwo) {
      setError("Please select both users");
      return;
    }
    if (userOne.value === userTwo.value) {
      setError("Cannot connect a user to themselves");
      return;
    }
    try {
      setError("");
      setLoading(true);
      await api.post("/core/connections/user/request", {
        user_id_one: userOne.value,
        user_id_two: userTwo.value,
      });
      navigate("/connections");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to create connection";
      console.error("[handleSubmit] Error:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <h2 className={styles.title}>New User Connection</h2>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="userOne">User One</label>
              <AsyncSelect
                id="userOne"
                placeholder="Search for a user..."
                loadOptions={searchUsers}
                onChange={(option) => setUserOne(option)}
                value={userOne}
                isSearchable
                cacheOptions
                defaultOptions={[]}
                className={styles.select}
                classNamePrefix="react-select"
                noOptionsMessage={() => "No users found"}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="userTwo">User Two</label>
              <AsyncSelect
                id="userTwo"
                placeholder="Search for a user..."
                loadOptions={searchUsers}
                onChange={(option) => setUserTwo(option)}
                value={userTwo}
                isSearchable
                cacheOptions
                defaultOptions={[]}
                className={styles.select}
                classNamePrefix="react-select"
                noOptionsMessage={() => "No users found"}
              />
            </div>
            <div className={styles.actions}>
              <button onClick={handleSubmit} className={styles.submitButton} disabled={loading}>
                Create Connection
              </button>
              <button onClick={() => navigate("/connections")} className={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewUserConnectionPage;
